import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
// components
import { FormattedMessage } from 'react-intl';
import Panel from 'components/Panel';
import Preloader from 'components/Preloader';
import NothingBox from 'components/NothingBox';
import SegmentedTabs from 'components/SegmentedTabs';
import ReactSelect from 'components/Select/ReactSelect';
import DeliveriesTab from './components/DeliveriesTab';
import BinsTab from './components/BinsTab';
// endpoints
import { getFeedDeliveriesFarms } from 'endpoints/feedDeliveries';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
// assets
import './FeedDeliveries.scss';

const tabs = { deliveries: 'deliveries', bins: 'bins' };

const FeedDeliveries = ({ measurementSystem, setPageOptions, setBreadcrumbs }) => {
  const [farms, setFarms] = useState([]);
  const [farmId, setFarmId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(tabs.deliveries);

  useEffect(() => {
    setPageOptions({ backLink: '' });
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.feedDeliveries" />,
        path: '/feed-deliveries',
        useLabelAsMobileTitle: true,
      },
    ]);
  }, [setPageOptions, setBreadcrumbs]);

  useEffect(() => {
    getFeedDeliveriesFarms({ per_page: 100 })
      .then((response) => {
        const loadedFarms = response.resources || [];
        setFarms(loadedFarms);
        // Farm defaults automatically when the user has access to only one farm
        if (loadedFarms.length === 1) setFarmId(loadedFarms[0].id);
      })
      .catch(toastResponseErrors)
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) return <Preloader isActive />;

  return (
    <Panel className="FeedDeliveries">
      <Panel.Heading title={<FormattedMessage id="general.feedDeliveries" />} />
      <Panel.Body noPadding>
        <div className="feed-deliveries-controls">
          {farms.length > 1 && (
            <ReactSelect
              className="farm-select"
              placeholder={<FormattedMessage id="general.selectFarm" />}
              options={farms}
              valueKey="id"
              labelKey="name"
              value={farmId}
              onChange={(option) => setFarmId(option ? option.id : null)}
              noInputMargin
            />
          )}
          <SegmentedTabs
            options={[
              { value: tabs.deliveries, label: <FormattedMessage id="general.deliveries" /> },
              { value: tabs.bins, label: <FormattedMessage id="general.bins" /> },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />
        </div>

        <NothingBox display={!farms.length}>
          <FormattedMessage tagName="h1" id="component.feedDeliveries.noFarms" />
        </NothingBox>

        {farmId && activeTab === tabs.deliveries && (
          <DeliveriesTab key={`deliveries-${farmId}`} farmId={farmId} measurementSystem={measurementSystem} />
        )}
        {farmId && activeTab === tabs.bins && (
          <BinsTab key={`bins-${farmId}`} farmId={farmId} measurementSystem={measurementSystem} />
        )}
        {!farmId && farms.length > 1 && (
          <div className="select-farm-hint">
            <FormattedMessage id="component.feedDeliveries.selectFarmHint" />
          </div>
        )}
      </Panel.Body>
    </Panel>
  );
};

FeedDeliveries.propTypes = {
  measurementSystem: T.oneOf(['imperial', 'metric']),
  setPageOptions: T.func.isRequired,
  setBreadcrumbs: T.func.isRequired,
};

export default connect(
  (state) => ({
    measurementSystem: state.auth.user.current_company.measurement_system,
  }), {
    setPageOptions,
    setBreadcrumbs,
  }
)(FeedDeliveries);
